import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, type CaptureMode } from "../lib/api.js";
import {
  enqueueStart,
  enqueueSegment,
  enqueueDeleteLast,
  enqueueFinalize,
  enqueueCancel,
  findOpenCapture,
} from "./captureQueue.js";

/**
 * Machine à états d'enregistrement (cahier des charges §7.3), couche métier réutilisable.
 *
 * Quatre états : au repos, enregistrement actif, en pause, finalisé. Le bouton principal alterne
 * uniquement enregistrement ↔ pause (jamais « stop ») ; chaque passage en pause **clôt le segment
 * courant**, aussitôt persisté dans la file hors-ligne (IndexedDB) puis envoyé séquentiellement
 * (§7.7). La finalisation transcrit chaque segment séparément côté serveur et concatène.
 *
 * Logique tap/maintien : un maintien enregistre tant que le doigt est posé et met en pause au
 * relâchement ; un appui bref lance un enregistrement verrouillé, l'appui bref suivant met en pause.
 * Pour une note cumulée, auto-finalisation après le délai en pause (réarmée à chaque reprise) ; pour
 * une note indépendante, seule la validation manuelle finalise. La mise en veille / le passage en arrière-plan
 * met l'enregistrement en pause (le segment courant est alors persisté).
 */

export type RecordingMode = CaptureMode;
export type RecordingPhase = "idle" | "recording" | "paused" | "finalizing";

const HOLD_MS = 350;
const LONG_CANCEL_MS = 550;
// Niveau crête (même mesure RMS que le VU-mètre) sous lequel un segment est considéré comme
// silencieux : le micro n'a rien capté. Un tel segment serait de toute façon rejeté par le
// transcripteur ; on l'ignore et on prévient l'utilisateur plutôt que de l'envoyer.
const SILENCE_PEAK = 0.02;
// Découpage temporel passé à MediaRecorder.start() : les données sont livrées par tranches pendant
// l'enregistrement au lieu d'un bloc unique à l'arrêt. Deux bénéfices : sur mobile, un onglet suspendu
// (veille / arrière-plan) ne fait plus perdre le segment courant ; et sur WebKit, on évite le tampon
// unique dont la livraison finale peut échouer, laissant un blob VIDE.
const TIMESLICE_MS = 1000;

interface RecRef {
  stream: MediaStream | null;
  recorder: MediaRecorder | null;
  chunks: Blob[];
  recorderMime: string;
  uploadMime: string;
  ext: string;
  localId: string | null;
  segCount: number;
  segDurations: number[];
  segStart: number;
  segPeak: number;
  /** Le VU-mètre a-t-il produit au moins une mesure sur ce segment ? Voir closeSegment. */
  meterRan: boolean;
  baseElapsed: number;
  elapsedTimer: number;
  autoTimer: number;
  audioCtx: AudioContext | null;
  /** Verrou d'écran actif pendant l'enregistrement (voir acquireWakeLock). */
  wakeLock: WakeLockSentinel | null;
  /** La dernière pause vient-elle de l'extinction de l'écran ? Sert à en avertir au retour. */
  pausedByHide: boolean;
  raf: number;
  enqueueChain: Promise<unknown>;
  mainDown: number;
  mainStarted: boolean;
  ignoreMainUp: boolean;
  cancelDown: number;
  phase: RecordingPhase;
  mode: RecordingMode;
}

/** Conteneurs qu'un MediaRecorder peut produire, et l'extension de fichier correspondante. */
const CONTAINER_EXT: Record<string, string> = {
  "audio/webm": "webm",
  "audio/ogg": "ogg",
  "audio/mp4": "m4a",
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
};

/**
 * Format effectivement retenu par le MediaRecorder, déduit de son `mimeType` (qui peut porter des
 * paramètres de codec : « audio/webm;codecs=opus »). Renvoie null si le conteneur est inconnu, seul
 * cas où l'on conserve la proposition initiale faute de mieux.
 */
function actualFormat(mime: string): { uploadMime: string; ext: string } | null {
  const base = mime.split(";")[0]?.trim().toLowerCase() ?? "";
  const ext = CONTAINER_EXT[base];
  return ext ? { uploadMime: base, ext } : null;
}

/**
 * PROPOSITION de format faite au MediaRecorder. Ce n'est qu'un souhait : le navigateur reste libre de
 * retenir autre chose, et lorsque aucun candidat n'est supporté on le laisse choisir son défaut
 * (`recorderMime` vide). Le format réellement obtenu se lit ensuite dans `MediaRecorder.mimeType` —
 * voir startSegment, qui corrige `uploadMime`/`ext` à partir de cette valeur.
 */
function pickMime(): { recorderMime: string; uploadMime: string; ext: string } {
  const candidates: [string, string, string][] = [
    ["audio/webm;codecs=opus", "audio/webm", "webm"],
    ["audio/webm", "audio/webm", "webm"],
    ["audio/mp4", "audio/mp4", "m4a"],
    ["audio/mpeg", "audio/mpeg", "mp3"],
  ];
  if (typeof MediaRecorder !== "undefined") {
    for (const [rec, up, ext] of candidates) {
      if (MediaRecorder.isTypeSupported(rec)) return { recorderMime: rec, uploadMime: up, ext };
    }
  }
  return { recorderMime: "", uploadMime: "audio/webm", ext: "webm" };
}

function errMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof DOMException) return "Micro indisponible ou accès refusé.";
  return fallback;
}

export interface RecordingOptions {
  finalizeDelaySeconds: number;
  audioBitsPerSecond?: number;
  /** Micro à utiliser (vide = périphérique par défaut). Appliqué à l'ouverture du flux. */
  micDeviceId?: string;
  onFinalized?: () => void;
}

export interface Recording {
  phase: RecordingPhase;
  mode: RecordingMode;
  segmentCount: number;
  elapsed: number;
  level: number;
  busy: boolean;
  error: string | null;
  /** Message informatif non bloquant (ex. segment silencieux ignoré). Se dissipe seul. */
  notice: string | null;
  canCancel: boolean;
  standaloneTitle: string;
  active: boolean;
  setMode: (m: RecordingMode) => void;
  setStandaloneTitle: (t: string) => void;
  pressMainDown: () => void;
  pressMainUp: () => void;
  pressCancelDown: () => void;
  pressCancelUp: () => void;
  validate: () => void;
}

export function useRecording(options: RecordingOptions): Recording {
  const [phase, setPhaseState] = useState<RecordingPhase>("idle");
  const [mode, setModeState] = useState<RecordingMode>("daily");
  const [segmentCount, setSegmentCount] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [standaloneTitle, setStandaloneTitle] = useState("");

  const delayRef = useRef(options.finalizeDelaySeconds);
  delayRef.current = options.finalizeDelaySeconds;
  const bitsRef = useRef(options.audioBitsPerSecond);
  bitsRef.current = options.audioBitsPerSecond;
  const micRef = useRef(options.micDeviceId);
  micRef.current = options.micDeviceId;
  const onFinalizedRef = useRef(options.onFinalized);
  onFinalizedRef.current = options.onFinalized;
  const titleRef = useRef("");
  titleRef.current = standaloneTitle;

  const r = useRef<RecRef>({
    stream: null, recorder: null, chunks: [], recorderMime: "", uploadMime: "audio/webm", ext: "webm",
    localId: null, segCount: 0, segDurations: [], segStart: 0, segPeak: 0, meterRan: false,
    baseElapsed: 0, elapsedTimer: 0, autoTimer: 0,
    audioCtx: null, wakeLock: null, pausedByHide: false, raf: 0, enqueueChain: Promise.resolve(),
    mainDown: 0, mainStarted: false, ignoreMainUp: false, cancelDown: 0, phase: "idle", mode: "daily",
  });

  const validateRef = useRef<() => Promise<void>>(async () => undefined);

  const setPhase = useCallback((p: RecordingPhase) => {
    r.current.phase = p;
    setPhaseState(p);
  }, []);

  const stopElapsedTimer = useCallback(() => {
    if (r.current.elapsedTimer) window.clearInterval(r.current.elapsedTimer);
    r.current.elapsedTimer = 0;
  }, []);
  const startElapsedTimer = useCallback(() => {
    stopElapsedTimer();
    r.current.elapsedTimer = window.setInterval(() => {
      setElapsed(r.current.baseElapsed + (Date.now() - r.current.segStart) / 1000);
    }, 100);
  }, [stopElapsedTimer]);
  const clearAutoTimer = useCallback(() => {
    if (r.current.autoTimer) window.clearTimeout(r.current.autoTimer);
    r.current.autoTimer = 0;
  }, []);

  const stopMeter = useCallback(() => {
    if (r.current.raf) cancelAnimationFrame(r.current.raf);
    r.current.raf = 0;
    setLevel(0);
  }, []);
  const startMeter = useCallback(() => {
    const stream = r.current.stream;
    if (!stream) return;
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    if (!r.current.audioCtx) r.current.audioCtx = new AC();
    const ctx = r.current.audioCtx;
    // Safari (iOS) crée le contexte à l'état « suspended » dès lors qu'on n'est plus dans la chaîne
    // directe du geste utilisateur — ce qui est notre cas, `getUserMedia` étant attendu avant. Un
    // contexte suspendu renvoie un signal plat : le VU-mètre resterait à zéro et, surtout, le segment
    // serait pris pour un silence et jeté (voir closeSegment). On le réveille systématiquement.
    if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
    const src = ctx.createMediaStreamSource(stream);
    const an = ctx.createAnalyser();
    an.fftSize = 256;
    src.connect(an);
    const buf = new Uint8Array(an.frequencyBinCount);
    const tick = () => {
      an.getByteTimeDomainData(buf);
      let sum = 0;
      for (const v of buf) {
        const x = (v - 128) / 128;
        sum += x * x;
      }
      const lvl = Math.min(1, Math.sqrt(sum / buf.length) * 3.2);
      r.current.meterRan = true;
      if (lvl > r.current.segPeak) r.current.segPeak = lvl;
      setLevel(lvl);
      r.current.raf = requestAnimationFrame(tick);
    };
    tick();
  }, []);

  /**
   * Verrou d'écran (Screen Wake Lock) : empêche la mise en veille automatique **pendant** un segment
   * en cours. Sans lui, l'écran s'éteint au bout de quelques dizaines de secondes, ce qui suspend
   * l'onglet et met l'enregistrement en pause (voir l'écouteur `visibilitychange` plus bas) — un
   * enregistrement long se coupait donc tout seul.
   *
   * Le verrou n'est demandé que sur un geste utilisateur (le démarrage d'un segment) et relâché dès
   * la pause : jamais gardé au repos. Le navigateur le relâche lui-même quand la page passe en
   * arrière-plan ; comme ce même passage met l'enregistrement en pause, on n'a rien à réarmer. API
   * absente (Safari < 16.4, Firefox ancien) : on continue sans, l'appel ne doit jamais faire échouer
   * un enregistrement.
   */
  const acquireWakeLock = useCallback(() => {
    if (r.current.wakeLock || !navigator.wakeLock) return;
    void navigator.wakeLock
      .request("screen")
      .then((lock) => {
        // Course : l'enregistrement a pu s'arrêter pendant l'attente. On relâche aussitôt.
        if (r.current.phase === "recording") r.current.wakeLock = lock;
        else void lock.release().catch(() => undefined);
      })
      .catch(() => undefined);
  }, []);
  const releaseWakeLock = useCallback(() => {
    const lock = r.current.wakeLock;
    r.current.wakeLock = null;
    if (lock) void lock.release().catch(() => undefined);
  }, []);

  const teardownStream = useCallback(() => {
    r.current.stream?.getTracks().forEach((t) => t.stop());
    r.current.stream = null;
    if (r.current.audioCtx) {
      void r.current.audioCtx.close().catch(() => undefined);
      r.current.audioCtx = null;
    }
  }, []);

  const resetAll = useCallback(() => {
    teardownStream();
    stopElapsedTimer();
    stopMeter();
    clearAutoTimer();
    releaseWakeLock();
    const c = r.current;
    c.recorder = null;
    c.chunks = [];
    c.localId = null;
    c.segCount = 0;
    c.segDurations = [];
    c.baseElapsed = 0;
    c.enqueueChain = Promise.resolve();
    c.mainStarted = false;
    c.ignoreMainUp = false;
    setPhase("idle");
    setSegmentCount(0);
    setElapsed(0);
    setBusy(false);
  }, [teardownStream, stopElapsedTimer, stopMeter, clearAutoTimer, releaseWakeLock, setPhase]);

  const closeSegment = useCallback(() => {
    const c = r.current;
    if (!c.recorder) return;
    const dur = Math.max(1, Math.round((Date.now() - c.segStart) / 1000));
    // Un segment n'est déclaré silencieux que si la mesure a RÉELLEMENT eu lieu : sans ce garde-fou,
    // un navigateur sans AudioContext exploitable ferait passer tous les segments pour silencieux et
    // les jetterait — perdant l'enregistrement au lieu de le transcrire.
    const silent = c.meterRan && c.segPeak < SILENCE_PEAK;
    stopElapsedTimer();
    stopMeter();
    releaseWakeLock(); // plus d'enregistrement en cours : l'écran peut de nouveau s'éteindre
    const mr = c.recorder;
    c.recorder = null;
    // Le tableau de morceaux et le format sont capturés ICI, pour CE segment. Les lire depuis `c` au
    // moment où `onstop` se déclenche exposerait à une course : startSegment réassigne `c.chunks` à la
    // reprise, et un `onstop` tardif bâtirait alors le blob sur le tableau vide du segment suivant.
    const chunks = c.chunks;
    const mime = c.uploadMime;
    const blobPromise = new Promise<Blob>((resolve) => {
      mr.onstop = () => resolve(new Blob(chunks, { type: mime }));
    });
    // On n'appelle PAS requestData() avant stop() : sur WebKit, vider le tampon juste avant l'arrêt
    // fait disparaître les dernières données au lieu de les livrer, et le blob final ressort VIDE
    // (symptôme observé sur iOS : segment de 0 octet, refusé par le transcripteur). La protection
    // contre la mise en veille de l'onglet est assurée bien plus sûrement par le découpage temporel
    // passé à start() — voir TIMESLICE_MS.
    try {
      mr.stop();
    } catch {
      /* déjà arrêté */
    }
    // Segment silencieux : le micro n'a rien capté. On l'IGNORE (ni file, ni décompte, ni segment) et
    // on prévient l'utilisateur — l'envoyer ne ferait que produire une transcription vide (rejetée).
    if (silent) {
      setElapsed(c.baseElapsed);
      setNotice("Aucun son détecté : ce segment a été ignoré. Vérifiez le micro (Réglages → Capture → Micro).");
      return;
    }
    c.baseElapsed += dur;
    setElapsed(c.baseElapsed);
    c.segDurations.push(dur);
    c.segCount += 1;
    setSegmentCount(c.segCount);
    // Persistance ordonnée : le blob est enregistré dans la file avant tout (et avant un finalize).
    c.enqueueChain = c.enqueueChain.then(async () => {
      const blob = await blobPromise;
      // Dernier filet : un blob vide ne part pas. L'envoyer ne produirait qu'un refus serveur (422)
      // que la file abandonne sans bruit — l'utilisateur croirait son enregistrement transcrit alors
      // qu'il est perdu. Mieux vaut le dire.
      if (!blob.size) {
        setNotice("Enregistrement vide : rien n'a été capté par le navigateur, le segment n'a pas été envoyé.");
        return;
      }
      if (c.localId) await enqueueSegment(c.localId, blob, dur, c.ext);
    });
  }, [stopElapsedTimer, stopMeter, releaseWakeLock]);

  const armAutoFinalize = useCallback(() => {
    clearAutoTimer();
    r.current.autoTimer = window.setTimeout(() => {
      void validateRef.current();
    }, Math.max(1, delayRef.current) * 1000);
  }, [clearAutoTimer]);

  const startSegment = useCallback(async () => {
    const c = r.current;
    clearAutoTimer();
    setError(null);
    setNotice(null);
    try {
      setBusy(true);
      if (!c.stream) {
        const audio: MediaTrackConstraints | boolean = micRef.current
          ? { deviceId: { exact: micRef.current } }
          : true;
        c.stream = await navigator.mediaDevices.getUserMedia({ audio });
        const m = pickMime();
        c.recorderMime = m.recorderMime;
        c.uploadMime = m.uploadMime;
        c.ext = m.ext;
      }
      if (!c.localId) {
        c.localId = await enqueueStart(c.mode, c.mode === "standalone" ? titleRef.current.trim() || undefined : undefined);
      }
      setBusy(false);
    } catch (e) {
      setBusy(false);
      setError(errMessage(e, "Démarrage de l'enregistrement impossible."));
      return;
    }
    const mrOpts: MediaRecorderOptions = {};
    if (c.recorderMime) mrOpts.mimeType = c.recorderMime;
    if (bitsRef.current) mrOpts.audioBitsPerSecond = bitsRef.current;
    const mr = new MediaRecorder(c.stream, mrOpts);
    c.recorder = mr;
    // Tableau propre à CE segment, refermé dans le gestionnaire : une tranche livrée en retard
    // alimente ainsi toujours son propre segment, jamais celui qui l'a remplacé.
    const chunks: Blob[] = [];
    c.chunks = chunks;
    c.segPeak = 0;
    c.meterRan = false;
    mr.ondataavailable = (e) => {
      if (e.data && e.data.size) chunks.push(e.data);
    };
    mr.start(TIMESLICE_MS);
    // Le format retenu n'est connu de façon fiable qu'ICI : pickMime ne fait qu'une proposition, et
    // Safari (iOS) répond faux à isTypeSupported pour TOUS nos candidats, y compris audio/mp4 qu'il
    // sait pourtant produire. On tombait alors sur le repli, qui laissait Safari enregistrer en
    // MP4/AAC tout en étiquetant l'envoi « audio/webm » : le serveur recevait un type mensonger et le
    // transcripteur refusait le segment. On lit donc le format réel, seule source de vérité.
    const actual = actualFormat(mr.mimeType);
    if (actual) {
      c.uploadMime = actual.uploadMime;
      c.ext = actual.ext;
    }
    c.segStart = Date.now();
    setPhase("recording");
    startElapsedTimer();
    startMeter();
    acquireWakeLock();
  }, [clearAutoTimer, setPhase, startElapsedTimer, startMeter, acquireWakeLock]);

  const pauseSegment = useCallback(() => {
    closeSegment();
    setPhase("paused");
    if (r.current.mode === "daily") armAutoFinalize();
  }, [closeSegment, setPhase, armAutoFinalize]);

  const validate = useCallback(async () => {
    const c = r.current;
    clearAutoTimer();
    if (c.phase === "recording") closeSegment();
    if (c.phase === "finalizing") return;
    setPhase("finalizing");
    setBusy(true);
    try {
      await c.enqueueChain; // s'assurer que tous les segments sont en file avant le finalize
      if (c.localId) {
        if (c.segCount === 0) await enqueueCancel(c.localId);
        else await enqueueFinalize(c.localId);
      }
      resetAll();
      onFinalizedRef.current?.();
    } catch (e) {
      setError(errMessage(e, "Finalisation impossible."));
      setBusy(false);
      setPhase("paused");
    }
  }, [clearAutoTimer, closeSegment, setPhase, resetAll]);
  validateRef.current = validate;

  const deleteLast = useCallback(async () => {
    const c = r.current;
    if (c.segCount === 0) return;
    setBusy(true);
    try {
      await c.enqueueChain;
      if (c.localId) await enqueueDeleteLast(c.localId);
      const removed = c.segDurations.pop() ?? 0;
      c.segCount = Math.max(0, c.segCount - 1);
      c.baseElapsed = Math.max(0, c.baseElapsed - removed);
      setSegmentCount(c.segCount);
      if (c.phase !== "recording") setElapsed(c.baseElapsed);
    } catch (e) {
      setError(errMessage(e, "Suppression du segment impossible."));
    } finally {
      setBusy(false);
    }
  }, []);

  const cancelAll = useCallback(async () => {
    const c = r.current;
    if (c.recorder) {
      c.recorder.onstop = null;
      try {
        c.recorder.stop();
      } catch {
        /* ignore */
      }
      c.recorder = null;
    }
    stopElapsedTimer();
    stopMeter();
    clearAutoTimer();
    setBusy(true);
    try {
      await c.enqueueChain.catch(() => undefined);
      if (c.localId) await enqueueCancel(c.localId);
    } catch (e) {
      setError(errMessage(e, "Annulation impossible."));
    }
    resetAll();
  }, [stopElapsedTimer, stopMeter, clearAutoTimer, resetAll]);

  const pressMainDown = useCallback(() => {
    const c = r.current;
    if (c.phase === "finalizing") return;
    if (c.phase === "recording") {
      c.ignoreMainUp = true;
      pauseSegment();
      return;
    }
    c.mainDown = Date.now();
    c.mainStarted = true;
    void startSegment();
  }, [pauseSegment, startSegment]);

  const pressMainUp = useCallback(() => {
    const c = r.current;
    if (c.ignoreMainUp) {
      c.ignoreMainUp = false;
      return;
    }
    if (c.mainStarted) {
      const held = Date.now() - c.mainDown > HOLD_MS;
      c.mainStarted = false;
      // Appui LONG (maintien) : relâcher termine ET finalise automatiquement (logique « talkie-walkie »).
      // Appui BREF (tap) : ne finalise pas ici — il a verrouillé l'enregistrement, c'est un second tap
      // qui met en pause (pressMainDown), puis validation manuelle ou auto-finalisation différée.
      if (held && c.phase === "recording") void validateRef.current();
    }
  }, []);

  const pressCancelDown = useCallback(() => {
    r.current.cancelDown = Date.now();
  }, []);
  const pressCancelUp = useCallback(() => {
    const held = Date.now() - r.current.cancelDown > LONG_CANCEL_MS;
    if (held) void cancelAll();
    else void deleteLast();
  }, [cancelAll, deleteLast]);

  const setMode = useCallback((m: RecordingMode) => {
    if (r.current.phase !== "idle" || r.current.localId) return;
    r.current.mode = m;
    setModeState(m);
  }, []);

  // Restauration d'une capture non finalisée (rechargement / sortie de veille).
  useEffect(() => {
    let cancelled = false;
    void findOpenCapture().then((open) => {
      if (cancelled || !open || r.current.localId) return;
      const c = r.current;
      c.localId = open.localId;
      c.mode = open.mode;
      c.segCount = open.segCount;
      setModeState(open.mode);
      setSegmentCount(open.segCount);
      setPhase("paused");
    });
    return () => {
      cancelled = true;
    };
  }, [setPhase]);

  // Un message informatif (segment silencieux ignoré) se dissipe seul après quelques secondes.
  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 6000);
    return () => window.clearTimeout(t);
  }, [notice]);

  /**
   * Mise en veille / arrière-plan : on met en pause (le segment courant est persisté).
   *
   * Le verrou d'écran (acquireWakeLock) empêche normalement l'écran de s'éteindre pendant un
   * enregistrement, mais il n'est pas garanti partout (API absente avant Safari 16.4, verrou révoqué
   * par le système, écran verrouillé à la main). Quand la pause survient malgré tout, elle doit être
   * DITE : sans message, on retrouve l'app en pause sans savoir à quel instant elle a cessé
   * d'écouter, donc sans savoir ce qui manque dans la note. Le message est posé au retour à l'écran,
   * seul moment où l'utilisateur peut le lire.
   */
  useEffect(() => {
    const onVisibility = () => {
      const c = r.current;
      if (document.visibilityState === "hidden") {
        if (c.phase === "recording") {
          c.pausedByHide = true;
          pauseSegment();
        }
      } else if (c.pausedByHide) {
        c.pausedByHide = false;
        setNotice("Écran éteint : l'enregistrement a été mis en pause. Ce qui a été dit avant est conservé ; reprenez avec le bouton.");
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [pauseSegment]);

  // Nettoyage des ressources locales à la destruction (n'efface pas la file).
  useEffect(() => {
    return () => {
      teardownStream();
      releaseWakeLock();
      if (r.current.elapsedTimer) window.clearInterval(r.current.elapsedTimer);
      if (r.current.autoTimer) window.clearTimeout(r.current.autoTimer);
      if (r.current.raf) cancelAnimationFrame(r.current.raf);
    };
  }, [teardownStream, releaseWakeLock]);

  const active = phase !== "idle";
  const canCancel = segmentCount > 0 || phase === "recording";

  return {
    phase, mode, segmentCount, elapsed, level, busy, error, notice, canCancel, standaloneTitle, active,
    setMode, setStandaloneTitle,
    pressMainDown, pressMainUp, pressCancelDown, pressCancelUp,
    validate: () => void validate(),
  };
}
